<template>
  <DrawerVue :refreshDisplay="refreshDisplay">
    <v-container>
      <v-row class="mb-4" align="center">
        <v-col cols="auto">
          <h2 v-if="digitalMediaFileData">
            {{ $t("basics.edit", { type: $t("basics.digitalMediaFile") }) }}
          </h2>
          <h2 v-else>
            {{ $t("basics.new", { type: $t("basics.digitalMediaFile") }) }}
          </h2>
        </v-col>
      </v-row>

      <v-form ref="digitalmediafileEditForm">
        <v-row>
          <v-col cols="12">
            <v-text-field
              variant="outlined"
              flat
              :rules="[requiredRule, max256]"
              v-model="name"
              :label="$t('basics.name') + '*'"
              :readonly="readonly"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              variant="outlined"
              flat
              :rules="[max5000]"
              v-model="description"
              :label="$t('basics.description')"
              :readonly="readonly"
            ></v-textarea>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" v-if="currFile" class="pb-0">
            <v-chip>
              {{ currFile }}
              <v-icon
                v-if="!readonly"
                class="ml-1"
                @click="currFile = undefined"
              >
                mdi-close
              </v-icon>
            </v-chip>
          </v-col>

          <v-col>
            <v-file-input
              variant="outlined"
              v-model="file"
              flat
              :clearable="false"
              :label="$t('basics.file') + '*'"
              :rules="[requiredFile, maxSizeRule]"
              :readonly="readonly"
              prepend-inner-icon="mdi-paperclip"
              :prepend-icon="''"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.odt,.ods,.odp,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.tif,.mp4,.mov,.avi,.mkv,.webm,.flv,.mp3,.wav,.ogg,.aac,.flac,.m4a,.zip"
              :multiple="false"
            ></v-file-input>
          </v-col>

          <v-col cols="auto" style="display: flex; align-items: center;">
            <v-icon
              @click="showInfo = !showInfo"
              class="mb-6"
              :color="showInfo ? 'primary' : ''"
            >
              mdi-information-outline
            </v-icon>
          </v-col>
        </v-row>

        <v-row v-if="showInfo" style="margin-top: 0px">
          <v-col class="text-caption grey--text pt-0">
            <v-card flat class="pa-4">
              <div class="font-weight-medium mb-1">
                <b>{{ $t("basics.availFileTypes") }}</b>
              </div>
              <div>
                <strong>{{ $t("basics.fileTypeDocuments") }}:</strong>
                PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, RTF, CSV, ODT, ODS, ODP
              </div>
              <div>
                <strong>{{ $t("basics.fileTypeImages") }}:</strong>
                JPG, JPEG, PNG, GIF, WEBP, BMP, TIFF, TIF
              </div>
              <div>
                <strong>{{ $t("basics.fileTypeVideos") }}:</strong>
                MP4, MOV, AVI, MKV, WEBM, FLV
              </div>
              <div>
                <strong>{{ $t("basics.fileTypeAudios") }}:</strong>
                MP3, WAV, OGG, AAC, FLAC, M4A
              </div>
              <div>
                <strong>{{ $t("basics.fileTypeArchives") }}:</strong>
                ZIP
              </div>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="isVideo">
          <v-row>
            <v-col cols="12" v-if="currThumbnail" class="pb-0">
              <v-chip>
                {{ currThumbnail }}
                <v-icon
                  v-if="!readonly"
                  class="ml-1"
                  @click="currThumbnail = undefined"
                >
                  mdi-close
                </v-icon>
              </v-chip>
            </v-col>

            <v-col>
              <v-file-input
                variant="outlined"
                v-model="thumbnail"
                flat
                :clearable="false"
                label="Thumbnail"
                :rules="[maxSizeRule]"
                :readonly="readonly"
                prepend-inner-icon="mdi-paperclip"
                :prepend-icon="''"
                accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.tif"
                :multiple="false"
              ></v-file-input>
            </v-col>

            <v-col cols="auto" style="display: flex; align-items: center;">
              <v-icon
                @click="showThumbnailInfo = !showThumbnailInfo"
                class="mb-6"
                :color="showThumbnailInfo ? 'primary' : ''"
              >
                mdi-information-outline
              </v-icon>
            </v-col>
          </v-row>

          <v-row v-if="showThumbnailInfo" style="margin-top: 0px">
            <v-col class="text-caption grey--text pt-0">
              <v-card flat class="pa-4">
                <div class="font-weight-medium mb-1">
                  <b>{{ $t("basics.availFileTypes") }}</b>
                </div>
                <div>
                  <strong>{{ $t("basics.fileTypeImages") }}:</strong>
                  JPG, JPEG, PNG, GIF, WEBP, BMP, TIFF, TIF
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <v-row v-if="!readonly">
          <v-col cols="12">
            <v-btn
              large
              block
              flat
              class="primaryButton"
              @click="confirm()"
              :loading="loading"
              ref="confirmButton"
              style="width: 100%"
            >
              <span>{{ $t("basics.confirm") }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </DrawerVue>
</template>

<script>
export default {
  name: "digitalmediafileEditDrawer",

  props: [
    "digitalMediaData",
    "digitalMediaFileData",
    "refreshDisplay",
    "readonly",
  ],

  emits: ["close"],

  data() {
    return {
      name: undefined,
      description: undefined,

      file: undefined,
      currFile: undefined,

      thumbnail: undefined,
      currThumbnail: undefined,

      loading: false,

      showInfo: false,
      showThumbnailInfo: false,

      maxSizeRule: (value) => {
        const selectedFile = this.getFile(value);

        return (
          !selectedFile ||
          this.$validFileSize(selectedFile) ||
          this.$i18n.t("messages.fileTooBig", { size: "250MB" })
        );
      },

      requiredRule: (value) =>
        (value !== null && value !== undefined && value !== "") ||
        this.$i18n.t("messages.required"),

      max256: (value) =>
        !value ||
        value.length <= 256 ||
        this.$i18n.t("messages.tooLong", { count: 256 }),

      max5000: (value) =>
        !value ||
        value.length <= 5000 ||
        this.$i18n.t("messages.tooLong", { count: 5000 }),

      requiredFile: (value) =>
        !!this.getFile(value) ||
        !!this.currFile ||
        this.$i18n.t("messages.required"),
    };
  },

  computed: {
    isVideo() {
      const selectedFile = this.getFile(this.file);
      const filename = selectedFile ? selectedFile.name : this.currFile;

      return /\.(mp4|mov|avi|mkv|webm|flv)$/i.test(filename || "");
    },
  },

  watch: {
    refreshDisplay() {
      this.setValues();

      this.$nextTick(() => {
        this.$refs.digitalmediafileEditForm?.resetValidation();
      });
    },

    isVideo(value) {
      if (!value) {
        this.thumbnail = undefined;
        this.showThumbnailInfo = false;
      }
    },
  },

  methods: {
    // Unterstützt sowohl File als auch File[] als v-model-Wert.
    getFile(value) {
      return Array.isArray(value) ? value[0] : value;
    },

    async confirm() {
      const { valid } =
        await this.$refs.digitalmediafileEditForm.validate();

      if (!valid) {
        return;
      }

      this.loading = true;

      const formData = new FormData();
      const selectedFile = this.getFile(this.file);
      const selectedThumbnail = this.getFile(this.thumbnail);

      formData.append("name", this.name);
      formData.append("description", this.description ?? "");
      formData.append("parent", this.digitalMediaData.id);

      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      if (this.isVideo && selectedThumbnail) {
        formData.append("thumbnail", selectedThumbnail);
      }

      if (this.digitalMediaFileData) {
        this.patchData(formData, this.digitalMediaFileData.id);
      } else {
        this.postData(formData);
      }
    },

    postData(formData) {
      this.axios
        .post(
          this.axios.defaults.baseURL + "media/digitalmediafiles/post/",
          formData
        )
        .then(() => {
          this.$emit("close", 1);
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(
            new CustomEvent("showErrorDisplay", { detail: e })
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    patchData(formData, id) {
      this.axios
        .patch(
          this.axios.defaults.baseURL +
            "media/digitalmediafiles/patch/" +
            id,
          formData
        )
        .then(() => {
          this.$emit("close", 1);
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(
            new CustomEvent("showErrorDisplay", { detail: e })
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setValues() {
      this.name = this.digitalMediaFileData?.name;
      this.description = this.digitalMediaFileData?.description;

      this.file = undefined;
      this.currFile = this.digitalMediaFileData?.file_name;

      this.thumbnail = undefined;
      this.currThumbnail = this.digitalMediaFileData?.thumbnail_name;

      this.showInfo = false;
      this.showThumbnailInfo = false;
    },
  },

  mounted() {
    this.setValues();
  },
};
</script>